// src/pages/auth/ForgotPassword.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { FaEnvelope, FaArrowLeft, FaSpinner, FaCheck, FaPaperPlane } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import assets from '../../assets';

const ForgotPassword = () => {
  const [step, setStep] = useState('email'); // 'email' or 'success'
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const { register, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setEmail(data.email);

      // Show loading toast
      const loadingToast = toast.loading('Sending reset instructions...');

      const response = await authAPI.forgotPassword({ email: data.email });
      
      toast.dismiss(loadingToast);

      if (response.success) {
        toast.success('Reset instructions sent to your email!');
        setStep('success');
      } else {
        throw new Error(response.message || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendEmail = async () => {
    try {
      setLoading(true);
      const loadingToast = toast.loading('Resending email...');
      
      const response = await authAPI.forgotPassword({ email });
      
      toast.dismiss(loadingToast);
      
      if (response.success) {
        toast.success('Email resent successfully!');
      } else {
        throw new Error(response.message || 'Failed to resend email');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to resend email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8 overflow-hidden">
      {/* Background Elements */}
      <div
        className="absolute inset-0 opacity-3"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 20%, #22c55e 2px, transparent 2px),
            radial-gradient(circle at 80% 80%, #fbbf24 2px, transparent 2px),
            radial-gradient(circle at 40% 60%, #ef4444 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px, 120px 120px, 60px 60px',
        }}
      />

      {/* Main Container */}
      <div className="relative flex flex-col items-center mx-auto lg:flex-row lg:max-w-5xl xl:max-w-6xl px-4 sm:px-6 lg:px-8 min-h-screen">
        
        {/* Image Column */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="w-full h-64 lg:w-1/2 lg:h-[500px] xl:h-[600px] relative overflow-hidden rounded-2xl mt-8 lg:mt-0"
        >
          <img
            src={assets.gallery?.competitions?.[3] || assets.heroImages?.primary}
            alt="Password Reset Support"
            className="h-full w-full object-cover"
          />
          
          <div className="absolute top-6 left-6 bg-black/20 backdrop-blur-md rounded-xl px-4 py-2">
            <span className="text-white text-sm font-semibold drop-shadow">
              Account Recovery
            </span>
          </div>
        </motion.div>

        {/* Form Column */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-lg bg-white md:max-w-2xl md:z-10 md:shadow-2xl md:absolute md:top-0 md:mt-32 lg:w-3/5 lg:right-0 lg:mt-20 lg:-ml-20 xl:mt-24 xl:-ml-12 rounded-2xl"
        >
          <div className="flex flex-col p-6 md:p-8">
            
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mb-4"
            >
              <Link
                to="/login"
                className="inline-flex items-center text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
              >
                <FaArrowLeft className="mr-2 w-3 h-3" />
                Back to Login
              </Link>
            </motion.div>

            {step === 'email' ? (
              <>
                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="mb-4"
                >
                  <span className="inline-block px-3 py-1.5 bg-blue-100 rounded-full border border-blue-200 text-blue-700 text-xs font-medium mb-4">
                    Password Recovery
                  </span>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.0 }}
                  className="text-xl lg:text-2xl font-bold text-gray-900 leading-tight mb-3"
                >
                  Forgot Your{' '}
                  <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
                    Password?
                  </span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="text-gray-600 text-sm leading-relaxed mb-6"
                >
                  No worries! Enter your email address and we'll send you a secure link with an OTP code to reset your password.
                </motion.p>

                {/* Email Form */}
                <motion.form
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.4 }}
                  onSubmit={handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Email Address</label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="email"
                        placeholder="your.email@school.com"
                        className="block w-full rounded-2xl border border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 focus:outline-none focus:ring-2 pl-10 pr-4 py-3 text-sm bg-gray-50 transition-colors"
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                            message: 'Please enter a valid email address'
                          }
                        })}
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs">{errors.email.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl font-bold hover:scale-105 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="mr-2 w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="mr-2 w-4 h-4" />
                        Send Reset Link
                      </>
                    )}
                  </button>
                </motion.form>
              </>
            ) : (
              <>
                {/* Success State */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaCheck className="w-8 h-8 text-green-600" />
                  </div>

                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                    Check Your Email!
                  </h1>

                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    We've sent a password reset link with an OTP code to <strong>{email}</strong>. 
                    Click the link and follow the instructions to reset your password.
                  </p>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <h3 className="text-sm font-semibold text-blue-900 mb-2">What's next?</h3>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li>• Click the reset link in the email</li>
                      <li>• Enter the OTP code provided</li>
                      <li>• Create a new secure password</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={resendEmail}
                      disabled={loading}
                      className="w-full px-6 py-3 border-2 border-blue-300 text-blue-700 rounded-2xl font-semibold hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <FaSpinner className="inline mr-2 w-4 h-4 animate-spin" />
                          Resending...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="inline mr-2 w-4 h-4" />
                          Resend Email
                        </>
                      )}
                    </button>

                    <Link
                      to="/login"
                      className="block w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold hover:scale-105 transition-all duration-300 hover:shadow-lg text-center"
                    >
                      Back to Login
                    </Link>
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;